// src/pages/Orders/ExecutiveOrders.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import orderService from '../../services/order.service';
import Toast from '../../components/Toast/Toast';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { ROLES } from '../../constants/roles';
import './OrderPages.css';

const STATUS_TABS = [
  { key: 'ALL',                  label: 'All' },
  { key: 'PENDING_CONFIRMATION', label: 'Pending Confirmation' },
  { key: 'CONFIRMED',            label: 'Confirmed' },
  { key: 'REJECTED',             label: 'Rejected' },
  { key: 'PARTIALLY_DISPATCHED', label: 'Partial' },
  { key: 'DISPATCHED',           label: 'Dispatched' },
  { key: 'DELIVERED',            label: 'Delivered' },
  { key: 'CANCELLED',            label: 'Cancelled' },
];

const STATUS_COLORS = {
  DRAFT:                { bg: '#f8f9fa', color: '#6c757d' },
  PENDING_CONFIRMATION: { bg: '#fff8e1', color: '#f57f17' },
  CONFIRMED:            { bg: '#e8f5e9', color: '#2e7d32' },
  REJECTED:             { bg: '#fce4ec', color: '#c62828' },
  PARTIALLY_DISPATCHED: { bg: '#e3f2fd', color: '#1565c0' },
  DISPATCHED:           { bg: '#e0f7fa', color: '#00695c' },
  DELIVERED:            { bg: '#e8f5e9', color: '#1b5e20' },
  FAILED_TO_DELIVER:    { bg: '#fce4ec', color: '#c62828' },
  CANCELLED:            { bg: '#fce4ec', color: '#b71c1c' },
};

// ✅ Roles that can confirm/deny orders
const CAN_CONFIRM_ROLES = [
  ROLES.EXECUTIVE,
  ROLES.AREA_MANAGER,
  ROLES.ZONAL_MANAGER,
  ROLES.ADMIN,
  ROLES.OFFICE_ADMIN,
  ROLES.SUPER_ADMIN,
];

const OrderStatusBadge = ({ status }) => {
  const style = STATUS_COLORS[status] || STATUS_COLORS.DRAFT;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: style.bg, color: style.color, whiteSpace: 'nowrap',
    }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const ExecutiveOrders = () => {
  const navigate    = useNavigate();
  const { user }    = useAuth();

  // ✅ Check if current user can confirm/deny
  const canConfirm  = CAN_CONFIRM_ROLES.includes(user?.role);

  const [orders,     setOrders]     = useState([]);
  const [activeTab,  setActiveTab]  = useState('ALL');
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [rejecting,  setRejecting]  = useState(null);

  const [confirmModal, setConfirmModal] = useState({ open: false, orderId: null, ordName: '' });
  const [rejectModal,  setRejectModal]  = useState({ open: false, orderId: null, ordName: '', reason: '' });

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await orderService.getOrders();
      if (res.success) setOrders(res.data || []);
    } catch (_) {
      setToast({ message: 'Failed to fetch orders', type: 'error' });
    } finally { setLoading(false); }
  };

  const filtered = activeTab === 'ALL'
    ? orders
    : orders.filter(o => o.orderStatus === activeTab);

  const openConfirmModal = (order) =>
    setConfirmModal({ open: true, orderId: order.id, ordName: order.orderId });

  const handleConfirmSubmit = async () => {
    setConfirming(confirmModal.orderId);
    setConfirmModal(prev => ({ ...prev, open: false }));
    try {
      await orderService.confirmOrder(confirmModal.orderId);
      setToast({ message: `Order ${confirmModal.ordName} confirmed!`, type: 'success' });
      fetchOrders();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to confirm', type: 'error' });
    } finally { setConfirming(null); }
  };

  const openRejectModal = (order) =>
    setRejectModal({ open: true, orderId: order.id, ordName: order.orderId, reason: '' });

  const handleRejectSubmit = async () => {
    setRejecting(rejectModal.orderId);
    setRejectModal(prev => ({ ...prev, open: false }));
    try {
      await orderService.rejectOrder(rejectModal.orderId, { reason: rejectModal.reason });
      setToast({ message: `Order ${rejectModal.ordName} rejected`, type: 'success' });
      fetchOrders();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to reject', type: 'error' });
    } finally { setRejecting(null); }
  };

  return (
    <div className="order-page-container" style={{ maxWidth: 1100 }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <ConfirmModal
        isOpen={confirmModal.open}
        title="Confirm Order"
        message={`Are you sure you want to confirm order ${confirmModal.ordName}? This will send it to the godown for dispatch.`}
        confirmLabel="✓ Confirm Order"
        confirmType="success"
        onConfirm={handleConfirmSubmit}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />

      <ConfirmModal
        isOpen={rejectModal.open}
        title="Deny Order"
        message={`You are about to deny order ${rejectModal.ordName}. The Sales Officer will be notified.`}
        confirmLabel="✕ Deny Order"
        confirmType="danger"
        onConfirm={handleRejectSubmit}
        onClose={() => setRejectModal(prev => ({ ...prev, open: false }))}
      >
        <label className="cm-reason-label">
          Reason for denial <span style={{ color: '#adb5bd', fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea
          className="cm-reason-input"
          rows={3}
          placeholder="e.g. Product out of stock, incorrect pricing..."
          value={rejectModal.reason}
          onChange={e => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
        />
      </ConfirmModal>

      <div className="order-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h4 className="order-title">Orders</h4>
          <p className="order-subtitle">
            {/* ✅ Role-aware subtitle */}
            {user?.role === ROLES.SALES_OFFICER
              ? 'Your placed orders'
              : 'Review and manage orders from your team'}
          </p>
        </div>
      </div>

      <div className="order-tabs">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            className={`order-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.key !== 'ALL' && (
              <span className="order-tab-count">
                {orders.filter(o => o.orderStatus === tab.key).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="order-table-container">
        {loading ? (
          <div className="order-loading">Loading orders...</div>
        ) : (
          <table className="order-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Dealer</th>
                <th>Godown</th>
                <th>Items</th>
                <th>Total</th>
                <th>SO</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? (
                filtered.map(order => (
                  <tr key={order.id}>
                    <td className="order-id-cell">{order.orderId}</td>
                    <td>{new Date(order.orderDate).toLocaleDateString('en-IN')}</td>
                    <td>{order.dealer?.dealerShopName || '—'}</td>
                    <td>{order.godown?.name || '—'}</td>
                    <td>
                      <span className="order-items-badge">
                        {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="order-amount">
                      ₹{Number(order.totalAmount).toLocaleString('en-IN')}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {order.createdByUser?.firstName} {order.createdByUser?.lastName}
                    </td>
                    <td>
                      <div>
                        <OrderStatusBadge status={order.orderStatus} />
                        {order.orderStatus === 'REJECTED' && order.rejectionReason && (
                          <div style={{ fontSize: 11, color: '#c62828', marginTop: 4, maxWidth: 140, wordBreak: 'break-word' }}>
                            Reason: {order.rejectionReason}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          className="order-btn-view"
                          onClick={() => navigate(`/orders/detail/${order.id}`)}
                        >
                          View
                        </button>

                        {/* ✅ Confirm/Deny only for roles that can confirm — NOT SO */}
                        {canConfirm && order.orderStatus === 'PENDING_CONFIRMATION' && (
                          <>
                            <button
                              className="order-btn-confirm"
                              onClick={() => openConfirmModal(order)}
                              disabled={confirming === order.id}
                            >
                              {confirming === order.id ? '...' : '✓ Confirm'}
                            </button>
                            <button
                              className="order-btn-reject"
                              onClick={() => openRejectModal(order)}
                              disabled={rejecting === order.id}
                            >
                              {rejecting === order.id ? '...' : '✕ Deny'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="order-no-data">No orders found</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExecutiveOrders;